import React, { FocusEvent, useCallback } from 'react';
import { Control, useController, UseControllerProps } from 'react-hook-form';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { memoizeField } from './memoizeField';
import FieldWrapper, { FormMethods } from './FieldWrapper';
import { DatePicker, DatePickerProps } from '@mui/x-date-pickers';
import useDialog from '../../helpers/hooks/useDialog';
import { Dayjs } from 'dayjs';
import { styled } from '@mui/material/styles';
import { MakeFieldsOptional } from '../../helpers/type.helper';

type Props = {
  id?: string;
  name: string;
  control?: Control
  onOpen?: () => void
  onClose?: () => void
  inputProps?: TextFieldProps
  controllerProps?: UseControllerProps
  errorMessage?: string
} & FormMethods
  & MakeFieldsOptional<DatePickerProps<any, any>, 'value' | 'onChange' | 'renderInput'>

const defaultFunction = () => {
};

const TextFieldStyled = styled(TextField)({
  zIndex: 0,
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: 'primary.main',
    },
  },
  '& .MuiFormLabel-root': {
    color: 'secondary.main',
  },
  '& .MuiFormLabel-asterisk': {
    color: 'error.main',
  },
});

const PaperProps = {
  sx: {
    '[aria-current="date"]': {
      color: 'rgba(0,0,0,0.38)',
    },
  },
};

const PopperProps = {
  sx: {
    '& .MuiPaper-root': {
      borderRadius: '20px',
    },
  },
};

function DateField(props: Props) {
  const { inputProps = {}, ...dateFieldProps } = props;

  const {
    id,
    name,
    value,
    label,
    control,
    formMethods,
    errorMessage,
    controllerProps,
    onChange = defaultFunction,
    onOpen = defaultFunction,
    onClose = defaultFunction,
    ...rest
  } = dateFieldProps;

  const dialog = useDialog();

  const { field } = useController({
    name,
    control: formMethods?.control || control,
    rules: controllerProps?.rules,
    defaultValue: controllerProps?.defaultValue,
    shouldUnregister: controllerProps?.shouldUnregister,
  });


  const handleChange = (date: Dayjs | null) => {
    onChange(date);

    const value = date?.toDate() || null;

    field.onChange(value);
  };

  const handleOpen = useCallback(() => {
    if (props.inputProps?.disabled) {
      return;
    }
    dialog.handleOpen();
    onOpen();
  }, [dialog, onOpen, props.inputProps?.disabled]);

  const handleClose = useCallback(() => {
    dialog.handleClose();
    onClose();
  }, [dialog, onClose]);

  const handleBlur = (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (inputProps.onBlur) {
      inputProps.onBlur(event);
    }

    if (field.onBlur) {
      field.onBlur();
    }
  };

  return (
    <DatePicker
      open={dialog.open}
      label={label}
      value={value || null}
      disableOpenPicker={true}
      disableHighlightToday={true}
      onChange={handleChange}
      onClose={handleClose}
      PaperProps={PaperProps}
      PopperProps={PopperProps}
      {...rest}
      renderInput={(params: TextFieldProps) => (
        <TextFieldStyled
          {...params}
          id={id}
          onBlur={handleBlur}
          fullWidth={true}
          helperText={errorMessage}
          onMouseDown={handleOpen}
          {...inputProps}
        />
      )}
    />
  );
}

export default FieldWrapper(memoizeField(DateField));
